import { UserTypes } from 'store/auth/types.ts'
import { floorTo, roundTo } from 'utils/number.ts'
import { CalculateInstalmentParams, CalculateInstalmentReturnType } from 'types/instalment.ts'

/**
 * Calculates the electricity/gas ratio from the individual instalment amounts.
 * The combined total is derived internally by summing electricity and gas.
 * If no gas amount is given, the full ratio is assigned to electricity.
 *
 * @param electricity - The electricity portion of the instalment.
 * @param gas - The gas portion of the instalment. Pass `0` for electricity-only customers.
 * @returns The electricity and gas ratios as fractions that sum to 1.
 *
 * @example
 * // Standard split
 * calculateInstalmentRatios(60, 40)
 * // => { electricity: 0.6, gas: 0.4 }
 *
 * @example
 * // Electricity-only customer (gas = 0)
 * calculateInstalmentRatios(80, 0)
 * // => { electricity: 1, gas: 0 }
 *
 * @example
 * // No instalment data yet (both 0)
 * calculateInstalmentRatios(0, 0)
 * // => { electricity: 1, gas: 0 }
 */
export const calculateInstalmentRatios = (electricity: number, gas: number): { electricity: number; gas: number } => {
  if (gas === 0) return { electricity: 1, gas: 0 }

  const total = electricity + gas
  return { electricity: electricity / total, gas: gas / total }
}

export const calculateInstalment = ({
  billShock,
  userType,
  initialMaxSliderBoundary,
  initialMinInstalment,
  currentInstalment,
  hasEffectiveGasContract,
  proposedInstalmentFromUrl
}: CalculateInstalmentParams): CalculateInstalmentReturnType => {
  let elRatio
  let ngRatio
  let minInstalment = 20
  let maxSliderBoundary = initialMaxSliderBoundary
  let billShockAmount: number | undefined = undefined
  let proposedInstalment: number | undefined = undefined

  if (billShock) {
    const { instalment, settlement } = billShock

    // Ratio's
    elRatio = instalment.estimatedRatio.electricity
    ngRatio = instalment.estimatedRatio.gas

    // Set the min instalment (if not super user)
    if (userType !== UserTypes.SUPER_USER) {
      minInstalment = Math.max(floorTo(5, instalment.minimum), 20)
    }

    // -- DETERMINE MAX SLIDER BOUNDARY --
    // The maximum input from the field is set on 100_000 (see edit-instalment/constants.ts)
    // Increase the proposed instalment by 50% rounded to nearest 50, if proposed is zero, set to 20
    const increasedProposedInstalment = instalment.proposed > 0 ? roundTo(50, instalment.proposed * 1.5) : 20

    // Calculate the max slider boundary based on proposed instalment
    // If the 50% increased proposed instalment is less than 500, set to 500 or 1.5 * current instalment (whichever is higher)
    // Otherwise, use the 50% increased proposed instalment
    const maxInstalmentFromProposed =
      increasedProposedInstalment < 500 ? (instalment.current > 500 ? instalment.current * 1.5 : 500) : increasedProposedInstalment

    // Set the max slider boundary
    // Always 0 on first render
    if (maxSliderBoundary === 0) {
      // If proposed instalment is between 0 and current instalment, set max to 1.5 * current instalment
      // Otherwise, set to calculated max from proposed instalment
      maxSliderBoundary = Math.round(
        instalment.proposed > 0 && instalment.proposed < currentInstalment.total
          ? currentInstalment.total * 1.5
          : roundTo(25, maxInstalmentFromProposed)
      )
    }

    // Set billshock amount & proposed instalment (if higher than current instalment)
    billShockAmount = settlement.billShockAmount
    if (instalment.proposed > currentInstalment.total) {
      proposedInstalment = instalment.proposed
    }
  } else {
    // NO BILLSHOCK DATA: no ratio's, no min/max, no proposed instalment, no billshock amount
    // elRatio = ratio calculated based on currentInstalment EXCEPT when currentInstalment is 0, than 0.5
    // ngRatio = ratio calculated based on currentInstalment EXCEPT when currentInstalment is 0, than 0.5
    // min = currentInstalment * 0.8 EXCEPT when superuser or when currentInstalment is 0 (= not returned from API), than 20
    // max = currentInstalment * 1.5 EXCEPT if smaller than 500, than 500
    // proposedInstalment & billshockAmount: not shown

    // Handle proposed instalment from URL
    if (Number(proposedInstalmentFromUrl)) {
      proposedInstalment = Number(proposedInstalmentFromUrl)
    }

    // Ratio's
    ;({ electricity: elRatio, gas: ngRatio } = calculateInstalmentRatios(
      currentInstalment.electricity,
      hasEffectiveGasContract ? currentInstalment.gas : 0
    ))

    // Set the min instalment if not super user
    if (userType !== UserTypes.SUPER_USER && currentInstalment.total !== 0) {
      minInstalment = Math.max(roundTo(5, currentInstalment.total * 0.8), 20)
    }

    // -- DETERMINE MAX INSTALMENT --
    // Increase the current instalment by 50%, used to determine max instalment
    const increasedCurrentInstalment = currentInstalment.total * 1.5

    // Set the max instalment
    // Always 0 on first render
    if (maxSliderBoundary === 0) {
      // If the 50% increased current instalment is less than 500, set to 500, otherwise use the increased current instalment
      // Round to nearest 50
      maxSliderBoundary = roundTo(50, Math.round(increasedCurrentInstalment < 500 ? 500 : increasedCurrentInstalment))
    }
  }

  return {
    min: initialMinInstalment === 0 ? minInstalment : initialMinInstalment,
    max: maxSliderBoundary,
    billShock: billShockAmount,
    proposed: proposedInstalment,
    proposedInstalmentFromUrl: proposedInstalmentFromUrl ? Number(proposedInstalmentFromUrl) : undefined,
    ratio: {
      electricity: elRatio,
      gas: ngRatio
    }
  }
}
